import React, { useEffect, useState, useMemo } from 'react';
import { useIsPhone } from '../hooks/useIsMobile';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Divider,
  CircularProgress,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar, { ToolbarActions } from '../components/ResponsiveToolbar';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ClearIcon from '@mui/icons-material/Clear';
import PrintIcon from '@mui/icons-material/Print';
import { format, startOfMonth, endOfMonth } from 'date-fns';

import { useQueryClient, useQuery } from '@tanstack/react-query';
import SupplierPaymentDialog from './dialogs/SupplierPaymentDialog';
import SupplierPurchaseDialog from './dialogs/SupplierPurchaseDialog';
import SupplierCreditDialog from './dialogs/SupplierCreditDialog';
import SupplierDebitDialog from './dialogs/SupplierDebitDialog';
import SupplierEditDialog from './dialogs/SupplierEditDialog';
import PaymentMethodRows from './components/PaymentMethodRows';
import MobileDialog from './dialogs/MobileDialog';

function getOneMonthBackRange() {
  const now = new Date();
  const end = new Date(now.getTime());
  end.setHours(23, 59, 59, 999);

  const start = new Date(now.getTime());
  start.setMonth(start.getMonth() - 1);
  start.setHours(0, 0, 0, 0);

  return { start, end };
}

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
});

// Purchase Detail Panel Component
function PurchaseDetailPanel({ purchaseId }) {
  const { data: purchaseDetails, isLoading, isError } = useQuery({
    queryKey: ['purchaseDetail', purchaseId],
    queryFn: async () => {
      const response = await API.get(`/supplier/purchase/?purchase_id=${purchaseId}`);
      return response.data;
    },
    enabled: !!purchaseId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (isError || !purchaseDetails) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        שגיאה בטעינת פרטי הרכישה
      </Alert>
    );
  }

  const hasItems = purchaseDetails.items && purchaseDetails.items.length > 0;
  const isNonILS = purchaseDetails.Currency && purchaseDetails.Currency !== 'ILS' && purchaseDetails.ForeignTotalAmount != null;
  const displayCurrency = isNonILS ? purchaseDetails.Currency : 'ILS';
  const fmtCurrency = (amount) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: displayCurrency }).format(amount);
  const fmtILS = (amount) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);

  return (
    <Box sx={{ p: 3 }}>
      {!hasItems ? (
        <Alert severity="info" sx={{ m: 2 }}>
          אין פריטים ברכישה זו
        </Alert>
      ) : (
        <>
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell align="center" width="50px">#</TableCell>
              <TableCell>שם מוצר</TableCell>
              <TableCell align="center">כמות</TableCell>
              <TableCell align="center">מחסן</TableCell>
              <TableCell align="right">מחיר יחידה</TableCell>
              <TableCell align="right">סה"כ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseDetails.items.map((item, index) => (
              <TableRow key={item.ItemID} hover>
                <TableCell align="center" width="50px" sx={{ fontWeight: 'bold' }}>
                  {index + 1}
                </TableCell>
                <TableCell>{item.ProductName || item.Product}</TableCell>
                <TableCell align="center">{item.Quantity}</TableCell>
                <TableCell align="center">
                  {item.StorageName || '–'}
                </TableCell>
                <TableCell align="right">
                  {fmtCurrency(item.UnitPrice)}
                </TableCell>
                <TableCell align="right">
                  {fmtCurrency(item.Subtotal)}
                </TableCell>
              </TableRow>
            ))}

            {purchaseDetails.Subtotal !== undefined && (
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                  סכום ביניים:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {fmtCurrency(purchaseDetails.Subtotal || 0)}
                </TableCell>
              </TableRow>
            )}

            {purchaseDetails.IsTaxable && purchaseDetails.TaxAmount > 0 && (
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', color: purchaseDetails.IsTaxIncluded ? 'text.secondary' : 'inherit' }}>
                  {purchaseDetails.IsTaxIncluded ? `מזה מע"ם (${purchaseDetails.TaxRate || 18}%):` : `מע"ם (${purchaseDetails.TaxRate || 18}%):`}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: purchaseDetails.IsTaxIncluded ? 'text.secondary' : 'inherit' }}>
                  {purchaseDetails.IsTaxIncluded ? '' : '+'}{fmtCurrency(purchaseDetails.TaxAmount || 0)}
                </TableCell>
              </TableRow>
            )}

            {purchaseDetails.ShippingCost > 0 && (
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold' }}>
                  משלוח:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {fmtCurrency(purchaseDetails.ShippingCost || 0)}
                </TableCell>
              </TableRow>
            )}
            
            <TableRow>
              <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                סה"כ לתשלום:
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                {purchaseDetails.Currency && purchaseDetails.Currency !== 'ILS' && purchaseDetails.ForeignTotalAmount != null ? (
                  <>
                    {new Intl.NumberFormat('he-IL', { style: 'currency', currency: purchaseDetails.Currency }).format(purchaseDetails.ForeignTotalAmount)}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(purchaseDetails.TotalAmount || 0)}
                    </Typography>
                  </>
                ) : (
                  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(purchaseDetails.TotalAmount || 0)
                )}
              </TableCell>
            </TableRow>
            {purchaseDetails.Currency && purchaseDetails.Currency !== 'ILS' && purchaseDetails.ExchangeRate && (
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ color: 'text.secondary' }}>
                  שער חליפין:
                </TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                  1 {purchaseDetails.Currency} = {purchaseDetails.ExchangeRate} ₪
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}
    </Box>
  );
}

// Supplier Transaction Detail Panel Component
function SupplierTransactionDetailPanel({ transaction, onEdit, onDelete, userRole, hideHeader = false }) {
  if (!transaction) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        אין מידע נוסף להצגה
      </Alert>
    );
  }

  // Define the fields to display (excluding Note - we'll handle it separately)
  const isForeignCurrency = transaction.Currency && transaction.Currency !== 'ILS' && transaction.ForeignAmount != null;
  const displayFields = [
    { key: 'Type', label: 'סוג' },
    { key: 'Amount', label: 'סכום' },
    { key: 'BalanceAfter', label: 'יתרה לאחר' },
    ...(isForeignCurrency ? [
      { key: 'Currency', label: 'מטבע' },
      { key: 'ForeignAmount', label: 'סכום במטבע מקור' },
      { key: 'ExchangeRate', label: 'שער חליפין' },
    ] : []),
    { key: 'InvoiceNumber', label: 'מספר חשבונית' },
    { key: 'ShippingDocument', label: 'תעודת משלוח' },
    { key: 'ActualDate', label: 'תאריך בפועל' },
    { key: 'Timestamp', label: 'תאריך רישום' },
  ];

  const isPurchase = transaction.Type === 'Purchase';

  const formatValue = (key, value) => {
    if (value === null || value === undefined || value === '') {
      return '–';
    }

    // Type translation
    if (key === 'Type') {
      const typeMap = {
        'Purchase': 'רכישה',
        'Payment': 'תשלום',
        'Credit': 'זיכוי',
        'Debit': 'חיוב',
      };
      return typeMap[value] || value;
    }

    // Date formatting
    if (key === 'Timestamp') {
      const date = new Date(value);
      return date.toLocaleString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    if (key === 'ActualDate') {
      const date = new Date(value);
      return date.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }

    // Currency formatting
    if (key === 'Amount') {
      const amount = parseFloat(value) || 0;
      const hasForeign = transaction.Currency && transaction.Currency !== 'ILS' && transaction.ForeignAmount != null;
      if (hasForeign) {
        const foreign = new Intl.NumberFormat('he-IL', { style: 'currency', currency: transaction.Currency }).format(parseFloat(transaction.ForeignAmount) || 0);
        const ils = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);
        return `${foreign} (${ils})`;
      }
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);
    }
    if (key === 'BalanceAfter') {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);
    }

    // Foreign currency fields
    if (key === 'Currency') {
      const currencyLabels = { USD: '$ דולר (USD)', EUR: '€ אירו (EUR)' };
      return currencyLabels[value] || value;
    }
    if (key === 'ForeignAmount') {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: transaction.Currency }).format(amount);
    }
    if (key === 'ExchangeRate') {
      return `1 ${transaction.Currency} = ${value} ₪`;
    }

    return value;
  };

  const hasContent = transaction.Note && transaction.Note.toString().trim() !== '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#f9f9f9' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        {!hideHeader && (
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.6rem' } }}>
            פרטי תנועה
          </Typography>
        )}
        {userRole === 'manager' && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={() => onEdit(transaction)}
              sx={{ fontSize: { xs: '0.9rem', sm: '1.3rem' }, px: { xs: 1.5, sm: 3 }, py: { xs: 0.5, sm: 1 }, flex: { xs: 1, sm: 'none' } }}
            >
              עריכה
            </Button>
            <Button
              variant="contained"
              color="error"
              size="medium"
              onClick={() => onDelete(transaction)}
              sx={{ fontSize: { xs: '0.9rem', sm: '1.3rem' }, px: { xs: 1.5, sm: 3 }, py: { xs: 0.5, sm: 1 }, flex: { xs: 1, sm: 'none' } }}
            >
              מחיקה
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Fields Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {displayFields.map((field) => (
          <Grid item xs={12} sm={6} md={4} key={field.key}>
            <Paper sx={{ p: 2, height: '100%', boxShadow: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  mb: 1,
                  fontSize: { xs: '0.9rem', sm: '1.2rem' }
                }}
              >
                {field.label}:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  wordBreak: 'break-word',
                  fontSize: { xs: '0.95rem', sm: '1.3rem' }
                }}
              >
                {formatValue(field.key, transaction[field.key])}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Payment Methods Section - only for Payment transactions - positioned ABOVE notes */}
      {transaction.Type === 'Payment' && transaction.payment_methods && transaction.payment_methods.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              אמצעי תשלום
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>סוג</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>סכום</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>פרטים</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.payment_methods.map((pm, index) => {
                  const typeLabels = {
                    cash: 'מזומן',
                    check: "צ'ק",
                    credit_card: 'כרטיס אשראי',
                    bank_transfer: 'העברה בנקאית',
                    other: 'אחר'
                  };

                  return (
                    <TableRow key={pm.SupplierPaymentMethodID || index}>
                      <TableCell align="center">{typeLabels[pm.Type] || pm.Type}</TableCell>
                      <TableCell align="center">
                        {pm.Currency && pm.Currency !== 'ILS' && pm.ForeignAmount != null ? (
                          <>
                            {new Intl.NumberFormat('he-IL', { style: 'currency', currency: pm.Currency }).format(pm.ForeignAmount)}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(pm.Amount)}
                            </Typography>
                          </>
                        ) : (
                          new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(pm.Amount)
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {pm.Type === 'check' && pm.check_details && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                              <Typography variant="body2">צ'ק #{pm.check_details.CheckNumber}</Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                                {pm.check_details.BankName && (
                                  <Typography variant="caption" color="text.secondary">
                                    בנק: {pm.check_details.BankName}
                                  </Typography>
                                )}
                                {pm.check_details.AccountNumber && (
                                  <Typography variant="caption" color="text.secondary">
                                    | חשבון: {pm.check_details.AccountNumber}
                                  </Typography>
                                )}
                                {pm.check_details.DueDate && (
                                  <Typography variant="caption" color="text.secondary">
                                    | לפירעון: {new Date(pm.check_details.DueDate).toLocaleDateString('he-IL')}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          {pm.Type === 'credit_card' && (
                            <Typography variant="body2">
                              {pm.CardType && `${pm.CardType} `}
                              {pm.CardLastFour && `****${pm.CardLastFour}`}
                            </Typography>
                          )}
                          {pm.Type === 'bank_transfer' && (
                            <Typography variant="body2">
                              {pm.BankName}
                              {pm.BankAccountLast4 && ` ****${pm.BankAccountLast4}`}
                              {pm.TransferDate && ` (${new Date(pm.TransferDate).toLocaleDateString('he-IL')})`}
                            </Typography>
                          )}
                          {pm.Type === 'cash' && (
                            <Typography variant="body2" color="text.secondary">-</Typography>
                          )}
                          {pm.Type === 'other' && pm.Notes && (
                            <Typography variant="body2">{pm.Notes}</Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Notes Section - EXACT same style as transactions page */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          הערות ופרטים
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      <Card sx={{ mb: 2, boxShadow: 1 }}>
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
              הערות
            </Typography>
          }
          sx={{
            backgroundColor: '#f5f1b7ff',
            py: 1,
          }}
        />
        <CardContent>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '1rem',
              lineHeight: 1.8,
              color: hasContent ? '#424242' : 'text.secondary',
              fontStyle: hasContent ? 'normal' : 'italic'
            }}
          >
            {hasContent ? transaction.Note : 'אין תוכן'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function SupplierDetails() {
  const { supplier_id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const isPhone = useIsPhone();
  const businessProfile = userProfile?.group?.business_profile || {};
  const queryClient = useQueryClient();

  // User-specific storage keys (prevents data leakage between users)
  const userId = userProfile?.username || 'unknown';

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [expandedNotes, setExpandedNotes] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editNotesValue, setEditNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);


  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Action dialogs state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [debitDialogOpen, setDebitDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

    // Transaction editing state
  const [editTransactionDialogOpen, setEditTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editTransactionMessage, setEditTransactionMessage] = useState('');
  const [editTransactionMessageType, setEditTransactionMessageType] = useState('success');
  const [updatingTransaction, setUpdatingTransaction] = useState(false);
  const [editingPaymentMethods, setEditingPaymentMethods] = useState([]);

  // Delete transaction state
  const [deleteTransactionDialogOpen, setDeleteTransactionDialogOpen] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [deleteTransactionPassword, setDeleteTransactionPassword] = useState('');
  const [deleteTransactionError, setDeleteTransactionError] = useState('');
  const [isDeletingTransaction, setIsDeletingTransaction] = useState(false);

  // Tab state - load from localStorage (user-specific + supplier-specific)
  const ACTIVE_TAB_KEY = `supplier-${supplier_id}-active-tab-${userId}`;
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const savedTab = localStorage.getItem(ACTIVE_TAB_KEY);
      return savedTab !== null ? parseInt(savedTab, 10) : 0;
    } catch (e) {
      console.error("Failed to load active tab from localStorage", e);
      return 0;
    }
  });

  // Transaction state
  const [transactions, setTransactions] = useState([]);
  // Mobile detail dialog state for transactions and purchases
  const [mobileTransactionDetail, setMobileTransactionDetail] = useState(null);
  const [mobilePurchaseDetail, setMobilePurchaseDetail] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('All');
  const [transactionStartDate, setTransactionStartDate] = useState(null);
  const [transactionEndDate, setTransactionEndDate] = useState(null);
  const [transactionRowCount, setTransactionRowCount] = useState(0);
  const [transactionPaginationModel, setTransactionPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [transactionSummaryFromApi, setTransactionSummaryFromApi] = useState(null);

  // Purchase state
  const [purchases, setPurchases] = useState([]);
  const [loadingPurchases, setLoadingPurchases] = useState(false);
  const [purchaseStartDate, setPurchaseStartDate] = useState(null);
  const [purchaseEndDate, setPurchaseEndDate] = useState(null);
  const [purchaseRowCount, setPurchaseRowCount] = useState(0);
  const [purchasePaginationModel, setPurchasePaginationModel] = useState({ page: 0, pageSize: 25 });
  const [purchaseIdSearch, setPurchaseIdSearch] = useState('');
  const [purchaseError, setPurchaseError] = useState('');

  const role = userProfile?.role || 'employee';

  // Grid API refs for state persistence
  const transactionsApiRef = useGridApiRef();
  const purchasesApiRef = useGridApiRef();

  // Storage keys for state persistence (user-specific + supplier-specific)
  const TRANSACTIONS_GRID_STATE_KEY = `supplier-${supplier_id}-transactions-grid-state-${userId}`;
  const PURCHASES_GRID_STATE_KEY = `supplier-${supplier_id}-purchases-grid-state-${userId}`;

  // Load initial grid states from localStorage
  const initialTransactionsGridState = React.useMemo(() => {
    try {
      const stateJSON = localStorage.getItem(TRANSACTIONS_GRID_STATE_KEY);
      if (stateJSON) {
        const parsed = JSON.parse(stateJSON);
        return {
          sorting: parsed.sorting || { sortModel: [{ field: 'ActualDate', sort: 'desc' }] },
          density: parsed.density || 'comfortable',
          pagination: parsed.pagination || {
            paginationModel: { page: 0, pageSize: 25 }
          }
        };
      }
    } catch (e) {
      console.error("Failed to load transactions grid state from localStorage", e);
    }
    return {
      sorting: { sortModel: [{ field: 'ActualDate', sort: 'desc' }] },
      pagination: { paginationModel: { page: 0, pageSize: 25 } },
      density: 'comfortable'
    };
  }, [TRANSACTIONS_GRID_STATE_KEY]);

  const initialPurchasesGridState = React.useMemo(() => {
    try {
      const stateJSON = localStorage.getItem(PURCHASES_GRID_STATE_KEY);
      if (stateJSON) {
        const parsed = JSON.parse(stateJSON);
        return {
          sorting: parsed.sorting || { sortModel: [{ field: 'PurchaseDate', sort: 'desc' }] },
          density: parsed.density || 'comfortable',
          pagination: parsed.pagination || {
            paginationModel: { page: 0, pageSize: 25 }
          }
        };
      }
    } catch (e) {
      console.error("Failed to load purchases grid state from localStorage", e);
    }
    return {
      sorting: { sortModel: [{ field: 'PurchaseDate', sort: 'desc' }] },
      pagination: { paginationModel: { page: 0, pageSize: 25 } },
      density: 'comfortable'
    };
  }, [PURCHASES_GRID_STATE_KEY]);

  const handleTransactionsStateChange = React.useCallback(() => {
    if (transactionsApiRef.current) {
      try {
        const state = transactionsApiRef.current.exportState();

        // Only save sorting for date and amount fields
        const allowedSortFields = ['Timestamp', 'ActualDate', 'Amount', 'Balance'];
        if (state.sorting?.sortModel) {
          state.sorting.sortModel = state.sorting.sortModel.filter(
            sort => allowedSortFields.includes(sort.field)
          );
        }

        localStorage.setItem(TRANSACTIONS_GRID_STATE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error('Failed to save transactions grid state:', error);
      }
    }
  }, [transactionsApiRef, TRANSACTIONS_GRID_STATE_KEY]);

  const handlePurchasesStateChange = React.useCallback(() => {
    if (purchasesApiRef.current) {
      try {
        const state = purchasesApiRef.current.exportState();

        // Only save sorting for date and total amount (not intermediate amounts)
        const allowedSortFields = ['PurchaseDate', 'TotalAmount'];
        if (state.sorting?.sortModel) {
          state.sorting.sortModel = state.sorting.sortModel.filter(
            sort => allowedSortFields.includes(sort.field)
          );
        }

        localStorage.setItem(PURCHASES_GRID_STATE_KEY, JSON.stringify(state));
      } catch (error) {
        console.error('Failed to save purchases grid state:', error);
      }
    }
  }, [purchasesApiRef, PURCHASES_GRID_STATE_KEY]);

  // Transaction summary from API (covers full filtered dataset, not just current page)
  const transactionSummary = useMemo(() => {
    if (transactionSummaryFromApi) {
      return transactionSummaryFromApi;
    }
    return {
      purchase_total: 0,
      payment_total: 0,
      credit_total: 0,
      debit_total: 0,
    };
  }, [transactionSummaryFromApi]);

  // Filter transactions by type
  const filteredTransactions = useMemo(() => {
    if (transactionTypeFilter === 'All') {
      return transactions;
    }
    return transactions.filter(t => t.Type === transactionTypeFilter);
  }, [transactions, transactionTypeFilter]);

  // Compute running foreign balance per transaction (for non-ILS suppliers)
  const foreignBalanceMap = useMemo(() => {
    if (!supplier?.Currency || supplier.Currency === 'ILS') return {};
    // Sort chronologically (oldest first) by ActualDate then TrID
    const sorted = [...transactions].sort((a, b) => {
      const dateA = new Date(a.ActualDate || a.Timestamp);
      const dateB = new Date(b.ActualDate || b.Timestamp);
      if (dateA.getTime() !== dateB.getTime()) return dateA - dateB;
      return a.TrID - b.TrID;
    });
    const map = {};
    let running = 0;
    for (const tx of sorted) {
      const fa = parseFloat(tx.ForeignAmount) || 0;
      if (tx.Type === 'Purchase' || tx.Type === 'Debit') {
        running += fa;
      } else {
        running -= fa;
      }
      map[tx.TrID] = parseFloat(running.toFixed(2));
    }
    return map;
  }, [transactions, supplier?.Currency]);

  async function fetchSupplierDetails() {
    
    try {
      setLoading(true);
      const url = `/supplier/?supplier_id=${supplier_id}`;
      const response = await API.get(url);
      setSupplier(response.data);
      setNotes(response.data.Notes || '');
      setEditNotesValue(response.data.Notes || '');
    } catch (error) {
      console.error('Error fetching supplier details:', error);
    } finally {
      setLoading(false);
    }
  }

  async function refreshSupplierDetails() {
    if (!supplier_id) return;
    
        try {
            setRefreshing(true); // ← Different from setLoading!
            const url = `/supplier/?supplier_id=${supplier_id}`;
            const response = await API.get(url);
            setSupplier(response.data);
            setNotes(response.data.Notes || '');
            setEditNotesValue(response.data.Notes || '');
        } catch (error) {
            console.error('Error refreshing supplier details:', error);
        } finally {
            setRefreshing(false);
    }
  }

  const fetchTransactions = async (page = transactionPaginationModel.page, pageSize = transactionPaginationModel.pageSize) => {
    try {
        setLoadingTransactions(true);
        const params = new URLSearchParams({
          supplier_id: supplier_id,
          page: (page + 1).toString(),
          page_size: pageSize.toString(),
        });

        if (transactionStartDate) {
          params.append('start_date', format(transactionStartDate, 'yyyy-MM-dd'));
        }
        if (transactionEndDate) {
          params.append('end_date', format(transactionEndDate, 'yyyy-MM-dd'));
        }

        const response = await API.get(`/supplier/transactions/?${params}`);
        setTransactions(response.data.results);
        setTransactionRowCount(response.data.count);
        if (response.data.summary) {
          setTransactionSummaryFromApi(response.data.summary);
        }
    } catch (error) {
        console.error('Error fetching transactions:', error);
    } finally {
        setLoadingTransactions(false);
    }
  }

    const fetchPurchases = async (page = purchasePaginationModel.page, pageSize = purchasePaginationModel.pageSize) => {
    try {
        setLoadingPurchases(true);
        setPurchaseError('');
        const params = new URLSearchParams({
          supplier_id: supplier_id,
          page: (page + 1).toString(),
          page_size: pageSize.toString(),
        });

        // If purchase ID is provided, use it; otherwise use dates (optional)
        if (purchaseIdSearch && purchaseIdSearch.trim() !== '') {
          params.append('purchase_id', purchaseIdSearch.trim());
        } else {
          if (purchaseStartDate) {
            params.append('start_date', format(purchaseStartDate, 'yyyy-MM-dd'));
          }
          if (purchaseEndDate) {
            params.append('end_date', format(purchaseEndDate, 'yyyy-MM-dd'));
          }
        }

        const response = await API.get(`/supplier/purchases/?${params}`);
        setPurchases(response.data.results);
        setPurchaseRowCount(response.data.count);
        setPurchaseError('');
    } catch (error) {
        console.error('Error fetching purchases:', error);
        setPurchaseError(error?.response?.data?.error || 'שגיאה בטעינת רכישות');
    } finally {
        setLoadingPurchases(false);
    }
    };

  // =============== PRINT FUNCTIONS ===============

  function formatCurrency(num, currencyCode = 'ILS') {
    const val = parseFloat(num) || 0;
    return val.toLocaleString('he-IL', { style: 'currency', currency: currencyCode || 'ILS' });
  }

  const handlePrintTransactions = () => {
    // Try to get sort model from DataGrid (desktop only); on mobile the ref
    // points at the MobileCardList wrapper (no exportState) so fall back to unsorted
    let sortModel = [];
    try {
      if (transactionsApiRef.current && typeof transactionsApiRef.current.exportState === 'function') {
        sortModel = transactionsApiRef.current.exportState()?.sorting?.sortModel || [];
      }
    } catch (e) {
      sortModel = [];
    }

    // Sort the filtered transactions based on the current sort model
    let sortedTransactions = [...filteredTransactions];

    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      sortedTransactions.sort((a, b) => {
        let aVal = a[field];
        let bVal = b[field];

        // Handle date fields
        if (field === 'Timestamp' || field === 'ActualDate') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else if (field === 'Amount' || field === 'Balance') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }

        if (sort === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }

    const htmlContent = generateTransactionsPrintHTML(supplier, sortedTransactions);
    openPrintWindow(htmlContent);
  };

  const handlePrintPurchases = () => {
    // Try to get sort model from DataGrid (desktop only); on mobile the ref
    // points at the MobileCardList wrapper (no exportState) so fall back to unsorted
    let sortModel = [];
    try {
      if (purchasesApiRef.current && typeof purchasesApiRef.current.exportState === 'function') {
        sortModel = purchasesApiRef.current.exportState()?.sorting?.sortModel || [];
      }
    } catch (e) {
      sortModel = [];
    }

    // Sort the purchases based on the current sort model
    let sortedPurchases = [...purchases];

    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      sortedPurchases.sort((a, b) => {
        let aVal = a[field];
        let bVal = b[field];

        // Handle date fields
        if (field === 'PurchaseDate') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else if (field === 'TotalAmount') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }

        if (sort === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }

    const htmlContent = generatePurchasesPrintHTML(supplier, sortedPurchases);
    openPrintWindow(htmlContent);
  };

  function generateTransactionsPrintHTML(supplierData, transactions) {
    const isSupplierForeign = supplierData?.Currency && supplierData.Currency !== 'ILS';
    const style = `
      <style>
        @media print {
          @page {
            margin: 10mm;
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
          }
        }
        body {
          direction: rtl;
          font-family: Arial, sans-serif;
          font-size: 16px;
        }
        table {
          border-collapse: collapse;
          width: 100%;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 6px;
          text-align: center;
          vertical-align: middle;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        h2, h3 {
          margin: 0;
          margin-bottom: 8px;
        }
        .section-header {
          margin-top: 24px;
          margin-bottom: 8px;
          font-size: 16px;
          font-weight: bold;
        }
        .supplier-info, .balance-summary, .transactions-table {
          margin-bottom: 24px;
        }
        .balance-positive {
          color: #c62828;
          font-weight: bold;
        }
        .balance-negative {
          color: #2e7d32;
          font-weight: bold;
        }
      </style>
    `;

    const supplierInfoTable = `
      <div class="supplier-info">
        <h2>פרטי ספק</h2>
        <table>
          <tbody>
            <tr>
              <th>שם הספק</th>
              <td>${supplierData?.SupplierName || ''}</td>
            </tr>
            <tr>
              <th>ח.פ./ע.מ.</th>
              <td>${supplierData?.SupplierBusinessID || ''}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    const balance = parseFloat(supplierData?.Balance || 0);
    const foreignBal = parseFloat(supplierData?.ForeignBalance || 0);
    const displayBal = isSupplierForeign ? foreignBal : balance;
    const balanceClass = displayBal > 0 ? 'balance-positive' : displayBal < 0 ? 'balance-negative' : '';
    const balanceLabel = displayBal > 0 ? 'חייב לספק' : displayBal < 0 ? 'הספק חייב לך' : 'אין יתרה';
    const primaryBalFormatted = isSupplierForeign
      ? formatCurrency(foreignBal, supplierData.Currency)
      : formatCurrency(balance);

    const balanceSummaryTable = `
      <div class="balance-summary">
        <h2>יתרה</h2>
        <table>
          <tbody>
            <tr>
              <th>יתרה נוכחית</th>
              <td class="${balanceClass}">${primaryBalFormatted}</td>
            </tr>
            ${isSupplierForeign ? `<tr>
              <th>שווי ב-₪</th>
              <td>${formatCurrency(balance)}</td>
            </tr>` : ''}
            <tr>
              <th>סטטוס</th>
              <td>${balanceLabel}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    const txTableHeader = `
      <th>סוג</th>
      <th>מספר חשבונית</th>
      <th>תעודת משלוח</th>
      <th>תאריך בפועל</th>
      <th>הערות</th>
      <th>סכום</th>
      <th>יתרה לאחר</th>
    `;

    // Compute foreign running balance for print
    const printForeignBalanceMap = {};
    if (isSupplierForeign) {
      const sorted = [...transactions].sort((a, b) => {
        const dateA = new Date(a.ActualDate || a.Timestamp);
        const dateB = new Date(b.ActualDate || b.Timestamp);
        if (dateA.getTime() !== dateB.getTime()) return dateA - dateB;
        return a.TrID - b.TrID;
      });
      let running = 0;
      for (const tx of sorted) {
        const fa = parseFloat(tx.ForeignAmount) || 0;
        if (tx.Type === 'Purchase' || tx.Type === 'Debit') running += fa;
        else running -= fa;
        printForeignBalanceMap[tx.TrID] = parseFloat(running.toFixed(2));
      }
    }

    const processedTransactions = transactions.map((tx) => {
      const typeMap = {
        'Purchase': 'רכישה',
        'Payment': 'תשלום',
        'Credit': 'זיכוי',
        'Debit': 'חיוב',
      };
      const typeLabel = typeMap[tx.Type] || tx.Type;

      // Amount: foreign primary, ILS hint
      const hasForeign = isSupplierForeign && tx.ForeignAmount != null;
      let amountDisplay;
      if (hasForeign) {
        amountDisplay = `${formatCurrency(tx.ForeignAmount, supplierData.Currency)}<br><small style="color:#666">${formatCurrency(tx.Amount)}</small>`;
      } else {
        amountDisplay = formatCurrency(tx.Amount || 0);
      }

      // Balance after: foreign primary, ILS hint
      const ilsBalance = parseFloat(tx.BalanceAfter || 0);
      let balanceDisplay;
      let balanceVal;
      if (isSupplierForeign) {
        const foreignBal = printForeignBalanceMap[tx.TrID] ?? 0;
        balanceVal = foreignBal;
        balanceDisplay = `${formatCurrency(foreignBal, supplierData.Currency)}<br><small style="color:#666">${formatCurrency(ilsBalance)}</small>`;
      } else {
        balanceVal = ilsBalance;
        balanceDisplay = formatCurrency(ilsBalance);
      }
      const balanceClass = balanceVal > 0 ? 'balance-positive' : balanceVal < 0 ? 'balance-negative' : '';

      const actualDate = tx.ActualDate ? new Date(tx.ActualDate).toLocaleDateString('he-IL') : '–';

      // Rate info for foreign currency
      const rateInfo = hasForeign ? `<br><small style="color:#666">שער: ${tx.ExchangeRate}</small>` : '';

      return `
        <tr>
          <td>${typeLabel}</td>
          <td>${tx.InvoiceNumber || '–'}</td>
          <td>${tx.ShippingDocument || '–'}</td>
          <td>${actualDate}</td>
          <td>${tx.Notes || '–'}${rateInfo}</td>
          <td>${amountDisplay}</td>
          <td class="${balanceClass}">${balanceDisplay}</td>
        </tr>
      `;
    }).join('');

    const transactionsHTML = `
      <div class="transactions-table">
        <h2>תנועות</h2>
        <p>סה"כ תנועות: ${transactions.length}</p>
        <table>
          <thead>
            <tr>${txTableHeader}</tr>
          </thead>
          <tbody>
            ${processedTransactions}
          </tbody>
        </table>
      </div>
    `;

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        <title>${supplierData?.SupplierName || 'ספק'} - תנועות</title>
        ${style}
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        ${supplierInfoTable}
        ${balanceSummaryTable}
        ${transactionsHTML}
      </body>
      </html>
    `;
  }

  function generatePurchasesPrintHTML(supplierData, purchasesList) {
    const isSupplierForeign2 = supplierData?.Currency && supplierData.Currency !== 'ILS';
    const style = `
      <style>
        @media print {
          @page {
            margin: 10mm;
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
          }
        }
        body {
          direction: rtl;
          font-family: Arial, sans-serif;
          font-size: 16px;
        }
        table {
          border-collapse: collapse;
          width: 100%;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 6px;
          text-align: center;
          vertical-align: middle;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        h2, h3 {
          margin: 0;
          margin-bottom: 8px;
        }
        .section-header {
          margin-top: 24px;
          margin-bottom: 8px;
          font-size: 16px;
          font-weight: bold;
        }
        .supplier-info, .balance-summary, .purchases-table {
          margin-bottom: 24px;
        }
        .balance-positive {
          color: #c62828;
          font-weight: bold;
        }
        .balance-negative {
          color: #2e7d32;
          font-weight: bold;
        }
      </style>
    `;

    const supplierInfoTable = `
      <div class="supplier-info">
        <h2>פרטי ספק</h2>
        <table>
          <tbody>
            <tr>
              <th>שם הספק</th>
              <td>${supplierData?.SupplierName || ''}</td>
            </tr>
            <tr>
              <th>ח.פ./ע.מ.</th>
              <td>${supplierData?.SupplierBusinessID || ''}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    const balance3 = parseFloat(supplierData?.Balance || 0);
    const foreignBalance3 = parseFloat(supplierData?.ForeignBalance || 0);
    const displayBal3 = isSupplierForeign2 ? foreignBalance3 : balance3;
    const balanceClass3 = displayBal3 > 0 ? 'balance-positive' : displayBal3 < 0 ? 'balance-negative' : '';
    const balanceLabel3 = displayBal3 > 0 ? 'חייב לספק' : displayBal3 < 0 ? 'הספק חייב לך' : 'אין יתרה';
    const primaryBalanceFormatted3 = isSupplierForeign2
      ? formatCurrency(foreignBalance3, supplierData.Currency)
      : formatCurrency(balance3);

    const balanceSummaryTable = `
      <div class="balance-summary">
        <h2>יתרה</h2>
        <table>
          <tbody>
            <tr>
              <th>יתרה נוכחית</th>
              <td class="${balanceClass3}">${primaryBalanceFormatted3}</td>
            </tr>
            ${isSupplierForeign2 ? `<tr>
              <th>שווי ב-₪</th>
              <td>${formatCurrency(balance3)}</td>
            </tr>` : ''}
            <tr>
              <th>סטטוס</th>
              <td>${balanceLabel3}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    const purchaseTableHeader = `
      <th>תאריך</th>
      <th>מזהה</th>
      <th>סכום ביניים</th>
      <th>משלוח</th>
      <th>מע"ם</th>
      <th>סה"כ</th>
    `;

    const processedPurchases = purchasesList.map((purchase) => {
      const purchaseDate = purchase.PurchaseDate
        ? new Date(purchase.PurchaseDate).toLocaleString('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '–';

      const isPurchaseFC = isSupplierForeign2 && purchase.ForeignTotalAmount != null;
      const subtotal = formatCurrency(purchase.Subtotal || 0);
      const shipping = formatCurrency(purchase.ShippingCost || 0);
      const tax = formatCurrency(purchase.TaxAmount || 0);
      let totalDisplay;
      if (isPurchaseFC) {
        totalDisplay = `${formatCurrency(purchase.ForeignTotalAmount, supplierData.Currency)}<br><small style="color:#666">${formatCurrency(purchase.TotalAmount)}</small>`;
      } else {
        totalDisplay = formatCurrency(purchase.TotalAmount || 0);
      }

      return `
        <tr>
          <td>${purchaseDate}</td>
          <td>${purchase.PurchaseID || '–'}</td>
          <td>${subtotal}</td>
          <td>${shipping}</td>
          <td>${tax}</td>
          <td><strong>${totalDisplay}</strong></td>
        </tr>
      `;
    }).join('');

    // Calculate totals
    const totalSubtotal = purchasesList.reduce((sum, p) => sum + (parseFloat(p.Subtotal) || 0), 0);
    const totalShipping = purchasesList.reduce((sum, p) => sum + (parseFloat(p.ShippingCost) || 0), 0);
    const totalTax = purchasesList.reduce((sum, p) => sum + (parseFloat(p.TaxAmount) || 0), 0);
    const totalAmount = purchasesList.reduce((sum, p) => sum + (parseFloat(p.TotalAmount) || 0), 0);
    const totalForeignAmount = isSupplierForeign2
      ? purchasesList.reduce((sum, p) => sum + (parseFloat(p.ForeignTotalAmount) || 0), 0)
      : 0;

    let totalDisplay;
    if (isSupplierForeign2 && totalForeignAmount > 0) {
      totalDisplay = `${formatCurrency(totalForeignAmount, supplierData.Currency)}<br><small style="color:#666">${formatCurrency(totalAmount)}</small>`;
    } else {
      totalDisplay = formatCurrency(totalAmount);
    }

    const purchasesHTML = `
      <div class="purchases-table">
        <h2>רכישות</h2>
        <p>סה"כ רכישות: ${purchasesList.length}</p>
        <table>
          <thead>
            <tr>${purchaseTableHeader}</tr>
          </thead>
          <tbody>
            ${processedPurchases}
            <tr style="background-color: #e0e0e0; font-weight: bold;">
              <td colspan="2">סה"כ</td>
              <td>${formatCurrency(totalSubtotal)}</td>
              <td>${formatCurrency(totalShipping)}</td>
              <td>${formatCurrency(totalTax)}</td>
              <td><strong>${totalDisplay}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        <title>${supplierData?.SupplierName || 'ספק'} - רכישות</title>
        ${style}
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        ${supplierInfoTable}
        ${balanceSummaryTable}
        ${purchasesHTML}
      </body>
      </html>
    `;
  }

  async function handleSaveNotes() {
    if (!supplier_id) return;
    
    try {
      setSavingNotes(true);
      const payload = {
        supplier_id: parseInt(supplier_id),
        Notes: editNotesValue,
      };
      await API.put('/supplier/update/', payload);
      
      setNotes(editNotesValue);
      setSupplier({ ...supplier, Notes: editNotesValue });
      setExpandedNotes(false);
      setEditingNotes(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('שגיאה בשמירת ההערות');
    } finally {
      setSavingNotes(false);
    }
  }

  const handleDeleteSupplier = async () => {
    if (!deletePassword) {
      alert('נא להזין סיסמה');
      return;
    }

    try {
      setDeleting(true);
      await API.delete('/supplier/delete/', {
        data: {
          supplier_id: parseInt(supplier_id),
          password: deletePassword,
        },
      });
      
      alert('הספק נמחק בהצלחה');
      navigate('/suppliers');
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });

    } catch (error) {
      console.error('Error deleting supplier:', error);
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('שגיאה במחיקת הספק');
      }
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDeletePassword('');
    }
  };

  const handleActionSuccess = () => {
    refreshSupplierDetails();
    fetchTransactions(transactionPaginationModel.page, transactionPaginationModel.pageSize);
    fetchPurchases(purchasePaginationModel.page, purchasePaginationModel.pageSize);
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  };

  // Handler for purchase ID search - clears dates when purchase ID is entered
  const handlePurchaseIdChange = (value) => {
    setPurchaseIdSearch(value);
    setPurchaseError(''); // Clear error when user makes changes
    if (value && value.trim() !== '') {
      // Clear dates when purchase ID is entered
      setPurchaseStartDate(null);
      setPurchaseEndDate(null);
    } else {
      // Reset to default date range when purchase ID is cleared
      const defaultRange = getOneMonthBackRange();
      setPurchaseStartDate(defaultRange.start);
      setPurchaseEndDate(defaultRange.end);
    }
  };

  // Handler for purchase date changes - clears purchase ID when dates are entered
  const handlePurchaseDateChange = (field, value) => {
    setPurchaseError(''); // Clear error when user makes changes
    if (field === 'start') {
      setPurchaseStartDate(value);
    } else {
      setPurchaseEndDate(value);
    }
    // Clear purchase ID when dates are entered
    if (value !== null) {
      setPurchaseIdSearch('');
    }
  };

  // Handler for tab changes - saves to localStorage
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, newValue.toString());
    } catch (error) {
      console.error('Failed to save active tab to localStorage:', error);
    }
  };

  useEffect(() => {
    if (supplier_id) {
      fetchSupplierDetails();
    }
  }, [supplier_id]);

  useEffect(() => {
    if (activeTab === 0 && supplier_id) {
      setTransactionPaginationModel(prev => ({ ...prev, page: 0 }));
      fetchTransactions(0, transactionPaginationModel.pageSize);
    }
  }, [activeTab, supplier_id, transactionStartDate, transactionEndDate]);

  useEffect(() => {
    if (activeTab === 1 && supplier_id) {
      setPurchasePaginationModel(prev => ({ ...prev, page: 0 }));
      fetchPurchases(0, purchasePaginationModel.pageSize);
    }
  }, [activeTab, supplier_id, purchaseStartDate, purchaseEndDate, purchaseIdSearch]);

  const transactionColumns = useMemo(() => [
    {
      field: 'ActualDate',
      headerName: 'תאריך בפועל',
      minWidth: 110,
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) return '–';
        const date = new Date(params.value);
        return date.toLocaleDateString('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      field: 'Type',
      headerName: 'סוג',
      minWidth: 120,
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const typeMap = {
          'Purchase': 'רכישה',
          'Payment': 'תשלום',
          'Credit': 'זיכוי',
          'Debit': 'חיוב',
        };
        const color = {
          'Purchase': '#1976d2',
          'Payment': '#2e7d32',
          'Credit': '#f57f17',
          'Debit': '#c62828',
        };
        return (
          <Chip
            label={typeMap[params.value] || params.value}
            sx={{
              backgroundColor: `${color[params.value] || '#757575'}15`,
              color: color[params.value] || '#757575',
              fontWeight: 'bold',
            }}
            size="small"
          />
        );
      },
    },
    {
      field: 'Amount',
      headerName: 'סכום',
      minWidth: 140,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        const hasForeign = row.Currency && row.Currency !== 'ILS' && row.ForeignAmount != null;
        if (hasForeign) {
          const foreignFormatted = new Intl.NumberFormat('he-IL', {
            style: 'currency', currency: row.Currency,
          }).format(parseFloat(row.ForeignAmount) || 0);
          const ilsFormatted = new Intl.NumberFormat('he-IL', {
            style: 'currency', currency: 'ILS',
          }).format(parseFloat(params.value) || 0);
          return (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{foreignFormatted}</Typography>
              <Typography variant="caption" color="text.secondary">{ilsFormatted}</Typography>
            </Box>
          );
        }
        const amount = parseFloat(params.value) || 0;
        return new Intl.NumberFormat('he-IL', {
          style: 'currency', currency: 'ILS',
        }).format(amount);
      },
    },
    {
      field: 'BalanceAfter',
      headerName: 'יתרה לאחר',
      minWidth: 140,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      renderCell: (params) => {
        const ilsBalance = parseFloat(params.value) || 0;
        const isFC = supplier?.Currency && supplier.Currency !== 'ILS';
        if (isFC) {
          const foreignBal = foreignBalanceMap[params.row.TrID] ?? null;
          const displayBal = foreignBal ?? ilsBalance;
          const color = displayBal > 0 ? '#c62828' : displayBal < 0 ? '#2e7d32' : '#757575';
          return (
            <Box sx={{ textAlign: 'center', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color, fontWeight: 'bold', lineHeight: 1.3 }}>
                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: supplier.Currency }).format(foreignBal ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(ilsBalance)}
              </Typography>
            </Box>
          );
        }
        const color = ilsBalance > 0 ? '#c62828' : ilsBalance < 0 ? '#2e7d32' : '#757575';
        return (
          <Box sx={{ display: 'inline-block', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}>
            <Typography sx={{ color, fontWeight: 'bold' }}>
              {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(ilsBalance)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'InvoiceNumber',
      headerName: 'מספר חשבונית',
      minWidth: 130,
      flex: 0.7,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
    },
    {
      field: 'ShippingDocument',
      headerName: 'תעודת משלוח',
      minWidth: 130,
      flex: 0.7,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
    },
    {
      field: 'Note',
      headerName: 'הערה',
      minWidth: 150,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
    },
  ], [supplier, foreignBalanceMap]);

  const purchaseColumns = useMemo(() => {
    const isFC = supplier?.Currency && supplier.Currency !== 'ILS';
    const fc = supplier?.Currency;

    // Format amount: for foreign currency suppliers, convert ILS amount to foreign using row's rate
    const formatAmountCell = (ilsValue, row) => {
      const val = parseFloat(ilsValue) || 0;
      if (isFC && row?.ExchangeRate && parseFloat(row.ExchangeRate) > 0) {
        const foreignVal = val / parseFloat(row.ExchangeRate);
        return new Intl.NumberFormat('he-IL', { style: 'currency', currency: fc }).format(foreignVal);
      }
      return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(val);
    };

    return [
    {
      field: 'PurchaseDate',
      headerName: 'תאריך',
      minWidth: 140,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) return '–';
        const date = new Date(params.value);
        return date.toLocaleString('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'PurchaseID',
      headerName: 'מזהה',
      minWidth: 80,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
    },
    {
      field: 'Subtotal',
      headerName: 'סכום ביניים',
      minWidth: 120,
      flex: 0.7,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      renderCell: (params) => formatAmountCell(params.value, params.row),
    },
    {
      field: 'ShippingCost',
      headerName: 'משלוח',
      minWidth: 100,
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      renderCell: (params) => formatAmountCell(params.value, params.row),
    },
    {
      field: 'TaxAmount',
      headerName: 'מע"ם',
      minWidth: 100,
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
      renderCell: (params) => formatAmountCell(params.value, params.row),
    },
    {
      field: 'TotalAmount',
      headerName: 'סה"כ',
      minWidth: 140,
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const row = params.row;
        const hasForeign = isFC && row.ForeignTotalAmount != null;
        if (hasForeign) {
          return (
            <Box sx={{ textAlign: 'center', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: fc }).format(row.ForeignTotalAmount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(params.value || 0)}
              </Typography>
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'inline-block', bgcolor: 'action.hover', px: 1.5, py: 0.5, borderRadius: 1 }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(params.value || 0)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'ExchangeRate',
      headerName: 'שער',
      minWidth: 80,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (!isFC || !params.value || parseFloat(params.value) === 1) return '–';
        return params.value;
      },
    },
    {
      field: 'Notes',
      headerName: 'הערות',
      minWidth: 150,
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      mobile: false,
    },
  ];
  }, [supplier]);

  // Mobile card renderer for transactions
  const supplierTransactionCardRenderer = (row) => {
    const amount = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(row.Amount || 0);
    const dateStr = row.ActualDate ? new Date(row.ActualDate).toLocaleDateString('he-IL') : '';
    const typeMap = { Purchase: 'רכישה', Payment: 'תשלום', Credit: 'זיכוי', Debit: 'חיוב' };
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {typeMap[row.Type] || row.Type}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: row.Type === 'Payment' ? 'success.main' : 'primary.main' }}>
            {amount}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {dateStr}
        </Typography>
      </Box>
    );
  };

  // Mobile card renderer for purchases
  const purchaseCardRenderer = (row) => {
    const total = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(row.TotalAmount || 0);
    const dateStr = row.PurchaseDate ? new Date(row.PurchaseDate).toLocaleDateString('he-IL') : '';
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            #{row.PurchaseID}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {total}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {dateStr}
        </Typography>
      </Box>
    );
  };

  const getDetailPanelContent = ({ row }) => {
    return (
      <PurchaseDetailPanel purchaseId={row.PurchaseID} />
    );
  };

  const getDetailPanelHeight = () => 'auto';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          ספק לא נמצא
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container
            maxWidth={false}
            sx={{
              width: '100%',
              px: { xs: 1, sm: 2 },
              my: { xs: 1, sm: 4 },
              fontSize: { xs: '1rem', sm: '1.4rem' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <IconButton
                onClick={() => navigate('/suppliers')}
                size="large"
                sx={{ color: 'primary.main' }}
              >
                <ArrowBackIcon sx={{ fontSize: '2.5rem' }} />
              </IconButton>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.75rem !important', sm: '3rem !important' },
                }}
              >
                {supplier?.SupplierName || 'פרטי ספק'}
              </Typography>
            </Box>

            {/* Cards Container */}
            <Box sx={{ maxWidth: { xs: '100%', md: '1200px' }, mr: 0, ml: { xs: 0, md: 'auto' }, mb: 4 }}>
              <Grid container spacing={3} alignItems="stretch">
                {/* Supplier Info Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardHeader
                      sx={{ backgroundColor: 'primary.main', color: 'white', py: 1.5 }}
                      title={
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          מידע ספק
                        </Typography>
                      }
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Grid container spacing={2}>
                        {/* Supplier Name */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            שם ספק:
                          </Typography>
                          <Typography variant="body1">{supplier?.SupplierName || ''}</Typography>
                        </Grid>

                        {/* Business ID */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            ח.פ./ע.מ.:
                          </Typography>
                          <Typography variant="body1">{supplier?.SupplierBusinessID || ''}</Typography>
                        </Grid>

                        {/* Contact Person */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            איש קשר:
                          </Typography>
                          <Typography variant="body1">{supplier?.ContactPerson || ''}</Typography>
                        </Grid>
                        
                        {/* Phone */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            טלפון:
                          </Typography>
                          <Typography variant="body1">{supplier?.Phone || supplier?.SupplierPhone || ''}</Typography>
                        </Grid>
                        
                        {/* Email */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            אימייל:
                          </Typography>
                          <Typography variant="body1">{supplier?.Email || supplier?.SupplierEmail || ''}</Typography>
                        </Grid>
                        
                        {/* Address */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            כתובת:
                          </Typography>
                          <Typography variant="body1">{supplier?.Address || supplier?.SupplierAddress || ''}</Typography>
                        </Grid>

                        
                        {/* Created Date */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            תאריך רישום:
                          </Typography>
                          <Typography variant="body1">
                            {supplier?.CreatedAt ? new Date(supplier.CreatedAt).toLocaleDateString('he-IL') : ''}
                          </Typography>
                        </Grid>
                        
                        {/* Updated Date */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            עדכון אחרון:
                          </Typography>
                          <Typography variant="body1">
                            {supplier?.UpdatedAt ? new Date(supplier.UpdatedAt).toLocaleDateString('he-IL') : ''}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                    {role === 'manager' && (
                      <Box sx={{ p: 2, textAlign: 'start', display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<DeleteIcon />}
                          sx={{
                            px: 3,
                            py: 1,
                            fontWeight: 'bold',
                            borderRadius: 2,
                            direction: 'ltr'
                          }}
                          onClick={() => setDeleteDialogOpen(true)}
                        >
                          מחיקה
                        </Button>
                        <Button
                        variant="contained"
                        color="secondary"
                        sx={{ px: 3, py: 1, fontWeight: 'bold', borderRadius: 2}}
                        onClick={() => setEditDialogOpen(true)}
                        >
                        עריכה
                        </Button>
                      </Box>
                    )}
                  </Card>
                </Grid>

                {/* Balance Card */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ borderRadius: 2, boxShadow: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardHeader
                      sx={{ backgroundColor: 'primary.main', color: 'white', py: 1.5 }}
                      title={
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          יתרה
                        </Typography>
                      }
                    />
                    <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 'bold',
                            color: (() => {
                              const displayBal = (supplier.Currency !== 'ILS' && supplier.ForeignBalance != null) ? supplier.ForeignBalance : supplier.Balance;
                              return displayBal > 0 ? '#c62828' : displayBal < 0 ? '#2e7d32' : '#757575';
                            })(),
                            fontSize: { xs: '2rem !important', sm: '3.5rem !important' },
                          }}
                        >
                          {supplier.Currency && supplier.Currency !== 'ILS' && supplier.ForeignBalance != null
                            ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: supplier.Currency }).format(supplier.ForeignBalance || 0)
                            : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(supplier.Balance || 0)
                          }
                        </Typography>
                        {supplier.Currency && supplier.Currency !== 'ILS' && (
                          <Typography variant="body1" sx={{ mt: 0.5, color: 'text.secondary' }}>
                            {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(supplier.Balance || 0)}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          {(() => {
                            const displayBal = (supplier.Currency !== 'ILS' && supplier.ForeignBalance != null) ? supplier.ForeignBalance : supplier.Balance;
                            return displayBal > 0 ? 'חייב לספק' : displayBal < 0 ? 'הספק חייב לך' : 'אין יתרה';
                          })()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Notes Section */}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ maxWidth: { xs: '100%', md: '1200px' }, mr: 0, ml: { xs: 0, md: 'auto' }, mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                הערות
              </Typography>
              <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4, p: 2 }}>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-line',
                      wordBreak: 'break-word',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      fontSize: '1.5rem !important',
                    }}
                  >
                    {notes || 'אין הערות'}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton
                      size="large"
                      onClick={() => {
                        setExpandedNotes(true);
                        setEditingNotes(false);
                      }}
                      sx={{ color: 'blue' }}
                    >
                      <FullscreenIcon fontSize="large" />
                    </IconButton>

                    {role === 'manager' && (
                      <Button
                        variant="contained"
                        onClick={() => {
                          setExpandedNotes(true);
                          setEditingNotes(true);
                        }}
                      >
                        עריכה
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>
            </Box>

            {/* Notes Dialog */}
            <MobileDialog
              open={expandedNotes}
              onClose={(event, reason) => {
                if (reason !== 'backdropClick' || !editingNotes) {
                  setExpandedNotes(false);
                  setEditingNotes(false);
                }
              }}
              PaperProps={{ 
                sx: { 
                  height: { xs: '100%', sm: '75vh' },
                  maxWidth: { xs: '100%', sm: '80vw' },
                  display: 'flex',
                  flexDirection: 'column',
                } 
              }}
            >
              <DialogTitle sx={{ fontSize: '2rem', fontWeight: 'bold', p: 2, flexShrink: 0 }}>
                הערות
              </DialogTitle>
              
              <DialogContent dividers sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', p: 0 }}>
                <Box sx={{ flex: 1, overflow: 'hidden', padding: '16px', paddingRight: '24px', display: 'flex', flexDirection: 'column' }}>
                  {!editingNotes ? (
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word', fontSize: '2rem', lineHeight: '1.5' }}>
                        {notes || 'אין הערות'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                      <textarea
                        value={editNotesValue}
                        onChange={(e) => setEditNotesValue(e.target.value)}
                        style={{
                          flex: 1,
                          width: '100%',
                          padding: '12px',
                          fontSize: '1.8rem',
                          lineHeight: '1.5',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          fontFamily: 'inherit',
                          resize: 'none',
                          outline: 'none',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 2 }}>
                {!editingNotes ? (
                  <>
                    {role === 'manager' && (
                      <Button variant="contained" onClick={() => setEditingNotes(true)} sx={{ fontSize: '1.6rem' }}>
                        עריכה
                      </Button>
                    )}
                    <Button onClick={() => { setExpandedNotes(false); setEditingNotes(false); }} sx={{ fontSize: '1.6rem' }}>
                      סגור
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="contained" onClick={handleSaveNotes} disabled={savingNotes} sx={{ fontSize: '1.6rem' }}>
                      {savingNotes ? <CircularProgress size={24} /> : 'שמור'}
                    </Button>
                    <Button onClick={() => { setEditNotesValue(notes); setEditingNotes(false); }} disabled={savingNotes} sx={{ fontSize: '1.6rem' }}>
                      ביטול
                    </Button>
                  </>
                )}
              </DialogActions>
            </MobileDialog>

            {/* Delete Dialog */}
            <MobileDialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ fontSize: '2rem', fontWeight: 'bold' }}>מחיקת ספק</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  פעולה זו תמחק את הספק לצמיתות. אנא הזן את הסיסמה שלך לאישור.
                </Alert>
                <TextField
                  label="סיסמה"
                  type="password"
                  fullWidth
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setDeleteDialogOpen(false); setDeletePassword(''); }}>
                  ביטול
                </Button>
                <Button variant="contained" color="error" onClick={handleDeleteSupplier} disabled={deleting}>
                  {deleting ? <CircularProgress size={24} /> : 'מחק'}
                </Button>
              </DialogActions>
            </MobileDialog>

            {/* Quick Actions Card */}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ maxWidth: { xs: '100%', md: '1200px' }, mr: 0, ml: { xs: 0, md: 'auto' }, mb: 4 }}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardHeader
                  sx={{ backgroundColor: 'primary.main', color: 'white', py: 1.5 }}
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      פעולות מהירות
                    </Typography>
                  }
                />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        startIcon={<PaymentIcon />}
                        sx={{ py: { xs: 1, sm: 2 }, fontSize: { xs: '0.85rem', sm: '1.4rem' }, fontWeight: 'bold', direction: 'ltr' }}
                        onClick={() => setPaymentDialogOpen(true)}
                      >
                        רישום תשלום
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="contained"
                        color="info"
                        fullWidth
                        startIcon={<ShoppingCartIcon />}
                        sx={{ py: { xs: 1, sm: 2 }, fontSize: { xs: '0.85rem', sm: '1.4rem' }, fontWeight: 'bold', direction: 'ltr' }}
                        onClick={() => setPurchaseDialogOpen(true)}
                      >
                        רישום רכישה
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        variant="contained"
                        color="warning"
                        fullWidth
                        startIcon={<CreditCardIcon />}
                        sx={{ py: { xs: 1, sm: 2 }, fontSize: { xs: '0.85rem', sm: '1.4rem' }, fontWeight: 'bold', direction: 'ltr' }}
                        onClick={() => setCreditDialogOpen(true)}
                      >
                        זיכוי
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>

            {/* Tabs Section */}
            <Divider sx={{ my: { xs: 2, sm: 4 } }} />
            <Card sx={{ borderRadius: 2, boxShadow: 3, p: { xs: 1, sm: 3 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isPhone ? 'scrollable' : 'fullWidth'}
                scrollButtons={isPhone ? 'auto' : false}
                allowScrollButtonsMobile
                sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 1.5, sm: 3 } }}
              >
                <Tab icon={<ReceiptIcon />} iconPosition="start" label="תנועות" sx={{ fontSize: { xs: '1rem !important', sm: '1.6rem !important' }, fontWeight: 'bold', minHeight: { xs: 48, sm: 64 } }} />
                <Tab icon={<LocalShippingIcon />} iconPosition="start" label="רכישות" sx={{ fontSize: { xs: '1rem !important', sm: '1.6rem !important' }, fontWeight: 'bold', minHeight: { xs: 48, sm: 64 } }} />
              </Tabs>

              {/* Transactions Tab */}
              {activeTab === 0 && (
                <Box>
                  <ResponsiveToolbar sx={{ mb: 3 }}>
                    <DatePicker
                      label="חודש"
                      value={transactionStartDate}
                      onChange={(newValue) => {
                        if (newValue) {
                          setTransactionStartDate(startOfMonth(newValue));
                          setTransactionEndDate(endOfMonth(newValue));
                        }
                      }}
                      views={['month', 'year']}
                      slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: 0, md: 160 }, width: { xs: '100%', md: 160 }, direction: 'ltr' } }, field: { clearable: true, onClear: () => { setTransactionStartDate(null); setTransactionEndDate(null); } } }}
                    />
                    <DatePicker
                      label="מתאריך"
                      value={transactionStartDate}
                      onChange={(newValue) => setTransactionStartDate(newValue)}
                      slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: 0, md: 180 }, width: { xs: '100%', md: 180 }, direction: 'ltr' } }, field: { clearable: true, onClear: () => setTransactionStartDate(null) } }}
                    />
                    <DatePicker
                      label="עד תאריך"
                      value={transactionEndDate}
                      onChange={(newValue) => setTransactionEndDate(newValue)}
                      slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: 0, md: 180 }, width: { xs: '100%', md: 180 }, direction: 'ltr' } }, field: { clearable: true, onClear: () => setTransactionEndDate(null) } }}
                    />
                    <TextField
                      select
                      label="סוג תנועה"
                      value={transactionTypeFilter}
                      onChange={(e) => setTransactionTypeFilter(e.target.value)}
                      size="small"
                      sx={{ width: { xs: '100%', sm: 180 } }}
                    >
                      <MenuItem value="All">הכל</MenuItem>
                      <MenuItem value="Purchase">רכישה</MenuItem>
                      <MenuItem value="Payment">תשלום</MenuItem>
                      <MenuItem value="Credit">זיכוי</MenuItem>
                    </TextField>
                    <ToolbarActions>
                      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchTransactions(0, transactionPaginationModel.pageSize)} sx={{ direction: 'ltr' }}>
                        סנון
                      </Button>
                      <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintTransactions} sx={{ direction: 'ltr' }}>
                        הדפס
                      </Button>
                    </ToolbarActions>
                  </ResponsiveToolbar>

                  <Box sx={{
                    width: '100%',
                    '.MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                      fontSize: '1.4rem',
                      whiteSpace: 'normal !important',
                      lineHeight: '1.2',  
                      overflow: 'visible', 
                    },
                    '.MuiDataGrid-cell': {
                      fontSize: '1.3rem',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}>
                    <ResponsiveDataView
                      apiRef={transactionsApiRef}
                      rows={filteredTransactions}
                      columns={transactionColumns}
                      cardRenderer={supplierTransactionCardRenderer}
                      onRowClick={(row) => setMobileTransactionDetail(row)}
                      emptyMessage="אין תוצאות"
                      density="comfortable"
                      getRowId={(row) => row.TrID}
                      getRowHeight={() => 'auto'}
                      loading={loadingTransactions}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      paginationMode="server"
                      rowCount={transactionRowCount}
                      paginationModel={transactionPaginationModel}
                      onPaginationModelChange={(model) => {
                        setTransactionPaginationModel(model);
                        fetchTransactions(model.page, model.pageSize);
                      }}
                      pageSizeOptions={[25, 50, 100]}
                      initialState={initialTransactionsGridState}
                      onStateChange={handleTransactionsStateChange}
                      localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                      getDetailPanelContent={({ row }) => (
                        <SupplierTransactionDetailPanel
                          transaction={row}
                          onEdit={(transaction) => {
                            setEditingTransaction(transaction);
                            // Initialize payment methods for Payment transactions
                            if (transaction.Type === 'Payment' && transaction.payment_methods?.length > 0) {
                              const isForeign = transaction.Currency && transaction.Currency !== 'ILS' && transaction.ForeignAmount != null;
                              const initialPaymentMethods = transaction.payment_methods.map(pm => ({
                                id: pm.SupplierPaymentMethodID,
                                type: pm.Type,
                                amount: (isForeign && pm.ForeignAmount != null ? pm.ForeignAmount : pm.Amount)?.toString() || '',
                                // Check fields
                                check_number: pm.check_details?.CheckNumber || '',
                                check_bank_name: pm.check_details?.BankName || '',
                                check_account_number: pm.check_details?.AccountNumber || '',
                                check_due_date: pm.check_details?.DueDate ? new Date(pm.check_details.DueDate) : null,
                                // Card fields
                                card_last_four: pm.CardLastFour || '',
                                card_type: pm.CardType || '',
                                // Transfer fields
                                bank_name: pm.BankName || '',
                                bank_account_last4: pm.BankAccountLast4 || '',
                                transfer_date: pm.TransferDate ? new Date(pm.TransferDate) : null,
                                notes: pm.Notes || '',
                              }));
                              setEditingPaymentMethods(initialPaymentMethods);
                            } else {
                              setEditingPaymentMethods([]);
                            }
                            setEditTransactionDialogOpen(true);
                          }}
                          onDelete={(transaction) => {
                            setDeletingTransaction(transaction);
                            setDeleteTransactionPassword('');
                            setDeleteTransactionError('');
                            setDeleteTransactionDialogOpen(true);
                          }}
                          userRole={role}
                        />
                      )}
                      getDetailPanelHeight={() => 'auto'}
                      getRowClassName={(params) => {
                        const type = params.row?.Type;
                        switch (type) {
                          case 'Purchase':
                            return 'purchase-row';
                          case 'Payment':
                            return 'payment-row';
                          case 'Credit':
                            return 'credit-row';
                          case 'Debit':
                            return 'debit-row';
                          default:
                            return '';
                        }
                      }}
                      sx={{
                        '& .purchase-row': {
                          backgroundColor: '#e3f2fd', // Light blue for purchases
                          '&:hover': {
                            backgroundColor: '#bbdefb',
                          },
                        },
                        '& .payment-row': {
                          backgroundColor: '#e8f5e9', // Light green for payments
                          '&:hover': {
                            backgroundColor: '#c8e6c9',
                          },
                        },
                        '& .credit-row': {
                          backgroundColor: '#fff8e1', // Light yellow for credits
                          '&:hover': {
                            backgroundColor: '#ffecb3',
                          },
                        },
                        '& .debit-row': {
                          backgroundColor: '#ffebee', // Light red for debits
                          '&:hover': {
                            backgroundColor: '#ffcdd2',
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Transaction Summary Section */}
                  {role === 'manager' && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'right' }}>
                        סיכום סינון נוכחי:
                      </Typography>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'flex-start',
                        alignItems: 'center', 
                        gap: 3,
                        flexWrap: 'wrap' 
                      }}>
                        {/* Purchase Summary */}
                        {(() => {
                          const isFC = supplier?.Currency && supplier.Currency !== 'ILS';
                          const foreignPurchase = isFC ? filteredTransactions.filter(t => t.Type === 'Purchase').reduce((sum, t) => sum + (parseFloat(t.ForeignAmount) || 0), 0) : 0;
                          const foreignPayment = isFC ? filteredTransactions.filter(t => t.Type === 'Payment').reduce((sum, t) => sum + (parseFloat(t.ForeignAmount) || 0), 0) : 0;
                          const foreignCredit = isFC ? filteredTransactions.filter(t => t.Type === 'Credit').reduce((sum, t) => sum + (parseFloat(t.ForeignAmount) || 0), 0) : 0;
                          const fc = supplier?.Currency;
                          return (
                            <>
                              <Box sx={{ backgroundColor: '#e3f2fd', padding: '8px 16px', borderRadius: 2, border: '1px solid #bbdefb', display: 'inline-block' }}>
                                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                                  סך רכישות:{' '}
                                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                    {isFC
                                      ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: fc }).format(foreignPurchase)
                                      : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(transactionSummary.purchase_total)
                                    }
                                  </Typography>
                                  {isFC && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                      {' '}({new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(transactionSummary.purchase_total)})
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>

                              <Box sx={{ backgroundColor: '#e8f5e9', padding: '8px 16px', borderRadius: 2, border: '1px solid #c8e6c9', display: 'inline-block' }}>
                                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                                  סך תשלומים:{' '}
                                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                    {isFC
                                      ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: fc }).format(foreignPayment)
                                      : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(transactionSummary.payment_total)
                                    }
                                  </Typography>
                                  {isFC && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                      {' '}({new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(transactionSummary.payment_total)})
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>

                              <Box sx={{ backgroundColor: '#fff8e1', padding: '8px 16px', borderRadius: 2, border: '1px solid #ffecb3', display: 'inline-block' }}>
                                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                                  סך זיכויים:{' '}
                                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                    {isFC
                                      ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: fc }).format(foreignCredit)
                                      : new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(transactionSummary.credit_total)
                                    }
                                  </Typography>
                                  {isFC && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                      {' '}({new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(transactionSummary.credit_total)})
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Purchases Tab */}
              {activeTab === 1 && (
                <Box>
                  {purchaseError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPurchaseError('')}>
                      {purchaseError}
                    </Alert>
                  )}
                  <ResponsiveToolbar sx={{ mb: 3 }}>
                    <TextField
                      label="מזהה רכישה"
                      value={purchaseIdSearch}
                      onChange={(e) => handlePurchaseIdChange(e.target.value)}
                      size="small"
                      sx={{ width: { xs: '100%', sm: 180 } }}
                      placeholder="חפש לפי מזהה רכישה"
                      InputProps={{
                        endAdornment: purchaseIdSearch ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => handlePurchaseIdChange('')}
                              edge="end"
                            >
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                    <DatePicker
                      label="חודש"
                      value={purchaseStartDate}
                      onChange={(newValue) => {
                        if (newValue) {
                          handlePurchaseDateChange('start', startOfMonth(newValue));
                          handlePurchaseDateChange('end', endOfMonth(newValue));
                        }
                      }}
                      views={['month', 'year']}
                      slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: 0, md: 160 }, width: { xs: '100%', md: 160 }, direction: 'ltr' } }, field: { clearable: true, onClear: () => { setPurchaseStartDate(null); setPurchaseEndDate(null); } } }}
                      disabled={purchaseIdSearch && purchaseIdSearch.trim() !== ''}
                    />
                    <DatePicker
                      label="מתאריך"
                      value={purchaseStartDate}
                      onChange={(newValue) => handlePurchaseDateChange('start', newValue)}
                      slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: 0, md: 180 }, width: { xs: '100%', md: 180 }, direction: 'ltr' } }, field: { clearable: true, onClear: () => setPurchaseStartDate(null) } }}
                      disabled={purchaseIdSearch && purchaseIdSearch.trim() !== ''}
                    />
                    <DatePicker
                      label="עד תאריך"
                      value={purchaseEndDate}
                      onChange={(newValue) => handlePurchaseDateChange('end', newValue)}
                      slotProps={{ textField: { size: 'small', sx: { minWidth: { xs: 0, md: 180 }, width: { xs: '100%', md: 180 }, direction: 'ltr' } }, field: { clearable: true, onClear: () => setPurchaseEndDate(null) } }}
                      disabled={purchaseIdSearch && purchaseIdSearch.trim() !== ''}
                    />
                    <ToolbarActions>
                      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchPurchases(0, purchasePaginationModel.pageSize)} sx={{ direction: 'ltr' }}>
                        סנון
                      </Button>
                      <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintPurchases} sx={{ direction: 'ltr' }}>
                        הדפס
                      </Button>
                    </ToolbarActions>
                  </ResponsiveToolbar>

                  <Box sx={{
                    width: '100%',
                    '.MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 'bold',
                      fontSize: '1.4rem',
                      whiteSpace: 'normal !important',
                      lineHeight: '1.2',  
                      overflow: 'visible', 
                    },
                    '.MuiDataGrid-cell': {
                      fontSize: '1.3rem',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}>
                    <ResponsiveDataView
                      apiRef={purchasesApiRef}
                      getRowHeight={() => 'auto'}
                      rows={purchases}
                      columns={purchaseColumns}
                      cardRenderer={purchaseCardRenderer}
                      onRowClick={(row) => setMobilePurchaseDetail(row)}
                      emptyMessage="אין תוצאות"
                      density="comfortable"
                      getRowId={(row) => row.PurchaseID}
                      loading={loadingPurchases}
                      autoHeight
                      disableRowSelectionOnClick
                      pagination
                      paginationMode="server"
                      rowCount={purchaseRowCount}
                      paginationModel={purchasePaginationModel}
                      onPaginationModelChange={(model) => {
                        setPurchasePaginationModel(model);
                        fetchPurchases(model.page, model.pageSize);
                      }}
                      pageSizeOptions={[25, 50, 100]}
                      initialState={initialPurchasesGridState}
                      onStateChange={handlePurchasesStateChange}
                      localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                      getDetailPanelContent={getDetailPanelContent}
                      getDetailPanelHeight={getDetailPanelHeight}
                      getRowClassName={() => 'purchase-row'}
                      sx={{
                        '& .purchase-row': {
                          backgroundColor: '#e3f2fd', // Light blue for purchases
                          '&:hover': {
                            backgroundColor: '#bbdefb',
                          },
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Card>

            <SupplierEditDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            supplier={supplier}
            onSuccess={handleActionSuccess}
            />

            {/* Action Dialogs */}
            <SupplierPaymentDialog
              open={paymentDialogOpen}
              onClose={() => setPaymentDialogOpen(false)}
              supplier={supplier}
              onSuccess={handleActionSuccess}
            />
            <SupplierPurchaseDialog
              open={purchaseDialogOpen}
              onClose={() => setPurchaseDialogOpen(false)}
              supplier={supplier}
              onSuccess={handleActionSuccess}
            />
            <SupplierCreditDialog
              open={creditDialogOpen}
              onClose={() => setCreditDialogOpen(false)}
              supplier={supplier}
              onSuccess={handleActionSuccess}
            />
            <SupplierDebitDialog
              open={debitDialogOpen}
              onClose={() => setDebitDialogOpen(false)}
              supplier={supplier}
              onSuccess={handleActionSuccess}
            />

            {/* ========== Mobile Transaction Detail Dialog ========== */}
            <MobileDialog
              open={Boolean(mobileTransactionDetail)}
              onClose={() => setMobileTransactionDetail(null)}
              maxWidth="md"
              fullWidth
            >
              {mobileTransactionDetail && (
                <>
                  <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      פרטי תנועה
                    </Typography>
                    <IconButton onClick={() => setMobileTransactionDetail(null)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <SupplierTransactionDetailPanel
                      transaction={mobileTransactionDetail}
                      hideHeader
                      onEdit={(transaction) => {
                        setMobileTransactionDetail(null);
                        setEditingTransaction(transaction);
                        if (transaction.Type === 'Payment' && transaction.payment_methods?.length > 0) {
                          const isForeign = transaction.Currency && transaction.Currency !== 'ILS' && transaction.ForeignAmount != null;
                          const initialPaymentMethods = transaction.payment_methods.map(pm => ({
                            id: pm.SupplierPaymentMethodID,
                            type: pm.Type,
                            amount: (isForeign && pm.ForeignAmount != null ? pm.ForeignAmount : pm.Amount)?.toString() || '',
                            check_number: pm.check_details?.CheckNumber || '',
                            check_bank_name: pm.check_details?.BankName || '',
                          }));
                          setEditingPaymentMethods(initialPaymentMethods);
                        } else {
                          setEditingPaymentMethods([]);
                        }
                        setEditTransactionDialogOpen(true);
                      }}
                      onDelete={(transaction) => {
                        setMobileTransactionDetail(null);
                        setDeletingTransaction(transaction);
                        setDeleteTransactionPassword('');
                        setDeleteTransactionError('');
                        setDeleteTransactionDialogOpen(true);
                      }}
                      userRole={role}
                    />
                  </DialogContent>
                </>
              )}
            </MobileDialog>

            {/* ========== Mobile Purchase Detail Dialog ========== */}
            <MobileDialog
              open={Boolean(mobilePurchaseDetail)}
              onClose={() => setMobilePurchaseDetail(null)}
              maxWidth="md"
              fullWidth
            >
              {mobilePurchaseDetail && (
                <>
                  <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      פרטי רכישה
                    </Typography>
                    <IconButton onClick={() => setMobilePurchaseDetail(null)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <PurchaseDetailPanel purchaseId={mobilePurchaseDetail.PurchaseID} />
                  </DialogContent>
                </>
              )}
            </MobileDialog>

            {/* Edit Transaction Dialog */}
            <MobileDialog
            open={editTransactionDialogOpen}
            onClose={() => {
                setEditTransactionDialogOpen(false);
                setEditingTransaction(null);
                setEditingPaymentMethods([]);
                setEditTransactionMessage('');
            }}
            maxWidth="md"
            fullWidth
            >
                <DialogTitle sx={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    עריכת תנועה
                </DialogTitle>
                <DialogContent>
                    {editingTransaction && (
                    <Box sx={{ mt: 2 }}>
                        {/* Success/Error Messages */}
                        {editTransactionMessage && (
                        <Alert 
                            severity={editTransactionMessageType} 
                            sx={{ mb: 2 }}
                            onClose={() => setEditTransactionMessage('')}
                        >
                            {editTransactionMessage}
                        </Alert>
                        )}
                        
                        {(() => {
                          const editIsForeign = editingTransaction.Currency && editingTransaction.Currency !== 'ILS' && editingTransaction.ForeignAmount != null;
                          const editCurrencySymbol = editIsForeign ? ({ USD: '$', EUR: '€' }[editingTransaction.Currency] || '₪') : '₪';
                          const editDisplayAmount = editIsForeign ? editingTransaction.ForeignAmount : editingTransaction.Amount;
                          return (
                        <Grid container spacing={2}>
                        {/* Amount field - editable only for non-Purchase transactions */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                            label={`סכום${editIsForeign ? ` (${editCurrencySymbol})` : ''}`}
                            fullWidth
                            type="number"
                            value={editDisplayAmount || ''}
                            onChange={(e) => {
                                if (editIsForeign) {
                                    const foreignVal = e.target.value;
                                    const rate = parseFloat(editingTransaction.ExchangeRate) || 1;
                                    setEditingTransaction({
                                        ...editingTransaction,
                                        ForeignAmount: foreignVal,
                                        Amount: foreignVal ? (parseFloat(foreignVal) * rate).toFixed(2) : '',
                                    });
                                } else {
                                    setEditingTransaction({
                                        ...editingTransaction,
                                        Amount: e.target.value
                                    });
                                }
                            }}
                            disabled={editingTransaction.Type === 'Purchase'}
                            helperText={editingTransaction.Type === 'Purchase' ? 'לא ניתן לערוך סכום של רכישה' : (editIsForeign && editDisplayAmount ? `≈ ₪${(parseFloat(editDisplayAmount) * (parseFloat(editingTransaction.ExchangeRate) || 1)).toFixed(2)}` : '')}
                            InputProps={{
                                readOnly: editingTransaction.Type === 'Purchase',
                            }}
                            />
                        </Grid>
                        {/* ActualDate field - editable only for non-Purchase transactions */}
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
                            <DatePicker
                                label="תאריך בפועל"
                                value={editingTransaction.ActualDate ? new Date(editingTransaction.ActualDate) : null}
                                onChange={(newValue) => setEditingTransaction({
                                ...editingTransaction,
                                ActualDate: newValue
                                })}
                                disabled={editingTransaction.Type === 'Purchase'}
                                slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'medium',
                                    helperText: editingTransaction.Type === 'Purchase' ? 'לא ניתן לערוך תאריך של רכישה' : '',
                                }
                                }}
                            />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                            label="מספר חשבונית"
                            fullWidth
                            value={editingTransaction.InvoiceNumber || ''}
                            onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                InvoiceNumber: e.target.value
                            })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                            label="תעודת משלוח"
                            fullWidth
                            value={editingTransaction.ShippingDocument || ''}
                            onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                ShippingDocument: e.target.value
                            })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                            label="הערה"
                            fullWidth
                            multiline
                            rows={3}
                            value={editingTransaction.Note || ''}
                            onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                Note: e.target.value
                            })}
                            />
                        </Grid>

                        {/* Payment Methods Section - only for Payment transactions */}
                        {editingTransaction.Type === 'Payment' && (
                          <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                              אמצעי תשלום
                            </Typography>
                            <PaymentMethodRows
                              paymentMethods={editingPaymentMethods}
                              setPaymentMethods={setEditingPaymentMethods}
                              totalAmount={parseFloat(editIsForeign ? editingTransaction.ForeignAmount : editingTransaction.Amount) || 0}
                              compact={true}
                              currencySymbol={editCurrencySymbol}
                            />
                          </Grid>
                        )}
                        </Grid>
                          );
                        })()}
                    </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                    onClick={() => {
                        setEditTransactionDialogOpen(false);
                        setEditingTransaction(null);
                        setEditingPaymentMethods([]);
                        setEditTransactionMessage('');
                    }}
                    sx={{ fontSize: '1.4rem' }}
                    disabled={updatingTransaction}
                    >
                    ביטול
                    </Button>
                    <Button
                    variant="contained"
                    onClick={async () => {
                        try {
                        setUpdatingTransaction(true);
                        setEditTransactionMessage('');

                        // Validate payment methods for Payment transactions
                        if (editingTransaction.Type === 'Payment' && editingPaymentMethods.length > 0) {
                            const pmTotal = editingPaymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
                            const editIsForeignTx = editingTransaction.Currency && editingTransaction.Currency !== 'ILS' && editingTransaction.ForeignAmount != null;
                            const transactionAmount = parseFloat(editIsForeignTx ? editingTransaction.ForeignAmount : editingTransaction.Amount) || 0;
                            if (Math.abs(pmTotal - transactionAmount) > 0.01) {
                                setEditTransactionMessage('סכום אמצעי התשלום חייב להיות שווה לסכום התנועה');
                                setEditTransactionMessageType('error');
                                setUpdatingTransaction(false);
                                return;
                            }
                        }

                        const updatePayload = {
                            transaction_id: editingTransaction.TrID,
                            invoice_number: editingTransaction.InvoiceNumber,
                            shipping_document: editingTransaction.ShippingDocument,
                            note: editingTransaction.Note,
                        };

                        // Only send amount and actual_date for non-Purchase transactions
                        if (editingTransaction.Type !== 'Purchase') {
                            updatePayload.amount = parseFloat(editingTransaction.Amount);
                            if (editingTransaction.ActualDate) {
                                updatePayload.actual_date = new Date(editingTransaction.ActualDate).toISOString();
                            }
                        }

                        // Include payment methods for Payment transactions
                        if (editingTransaction.Type === 'Payment' && editingPaymentMethods.length > 0) {
                            updatePayload.payment_methods = editingPaymentMethods.map(pm => ({
                                id: pm.id || null,
                                type: pm.type,
                                amount: parseFloat(pm.amount) || 0,
                                // Check fields
                                ...(pm.type === 'check' && {
                                    check_number: pm.check_number,
                                    check_bank_name: pm.check_bank_name,
                                    check_account_number: pm.check_account_number,
                                    check_due_date: pm.check_due_date ? format(pm.check_due_date, 'yyyy-MM-dd') : null,
                                }),
                                // Card fields
                                ...(pm.type === 'credit_card' && {
                                    card_last_four: pm.card_last_four,
                                    card_type: pm.card_type,
                                }),
                                // Transfer fields
                                ...(pm.type === 'bank_transfer' && {
                                    bank_name: pm.bank_name,
                                    bank_account_last4: pm.bank_account_last4,
                                    transfer_date: pm.transfer_date ? format(pm.transfer_date, 'yyyy-MM-dd') : null,
                                }),
                                notes: pm.notes || '',
                            }));
                        }

                        await API.put('/supplier/transaction/update/', updatePayload);

                        setEditTransactionMessage('התנועה עודכנה בהצלחה');
                        setEditTransactionMessageType('success');

                        // Refresh data
                        fetchTransactions(transactionPaginationModel.page, transactionPaginationModel.pageSize);
                        refreshSupplierDetails();

                        // Close dialog after a short delay
                        setTimeout(() => {
                            setEditTransactionDialogOpen(false);
                            setEditingTransaction(null);
                            setEditingPaymentMethods([]);
                            setEditTransactionMessage('');
                        }, 1500);

                        } catch (error) {
                        console.error('Error updating transaction:', error);
                        setEditTransactionMessage(
                            error.response?.data?.error || 'שגיאה בעדכון התנועה'
                        );
                        setEditTransactionMessageType('error');
                        } finally {
                        setUpdatingTransaction(false);
                        }
                    }}
                    sx={{ fontSize: '1.4rem' }}
                    disabled={updatingTransaction}
                    >
                    {updatingTransaction ? <CircularProgress size={24} /> : 'שמור'}
                    </Button>
                </DialogActions>
            </MobileDialog>

            {/* Delete Transaction Confirmation Dialog */}
            <MobileDialog
              open={deleteTransactionDialogOpen}
              onClose={() => {
                if (!isDeletingTransaction) {
                  setDeleteTransactionDialogOpen(false);
                  setDeletingTransaction(null);
                  setDeleteTransactionPassword('');
                  setDeleteTransactionError('');
                }
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontSize: '2rem', fontWeight: 'bold', color: 'error.main' }}>
                מחיקת תנועה
              </DialogTitle>
              <DialogContent>
                {deletingTransaction && (
                  <Box sx={{ mt: 2 }}>
                    {/* Warning Alert */}
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {deletingTransaction.Type === 'Purchase' ? (
                        <>
                          <strong>שים לב:</strong> מחיקת רכישה זו תבצע את הפעולות הבאות:
                          <ul style={{ margin: '8px 0 0 0', paddingRight: '20px' }}>
                            <li>היפוך המלאי שנוסף ברכישה זו</li>
                            <li>מחיקת פרטי הרכישה והפריטים</li>
                            <li>עדכון יתרת הספק</li>
                          </ul>
                        </>
                      ) : (
                        'האם אתה בטוח שברצונך למחוק תנועה זו? פעולה זו אינה הפיכה ותשפיע על יתרת הספק.'
                      )}
                    </Alert>

                    {/* Transaction Details */}
                    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>סוג תנועה:</strong>{' '}
                        {deletingTransaction.Type === 'Purchase' ? 'רכישה' :
                         deletingTransaction.Type === 'Payment' ? 'תשלום' :
                         deletingTransaction.Type === 'Credit' ? 'זיכוי' :
                         deletingTransaction.Type === 'Debit' ? 'חיוב' : deletingTransaction.Type}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>סכום:</strong>{' '}
                        {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(deletingTransaction.Amount || 0)}
                      </Typography>
                      <Typography variant="body1">
                        <strong>ספק:</strong> {supplier?.SupplierName}
                      </Typography>
                    </Paper>

                    {/* Error Message */}
                    {deleteTransactionError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {deleteTransactionError}
                      </Alert>
                    )}

                    {/* Password Confirmation */}
                    <TextField
                      label="הזן סיסמה לאישור"
                      type="password"
                      fullWidth
                      value={deleteTransactionPassword}
                      onChange={(e) => setDeleteTransactionPassword(e.target.value)}
                      disabled={isDeletingTransaction}
                      autoFocus
                    />
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button
                  onClick={() => {
                    setDeleteTransactionDialogOpen(false);
                    setDeletingTransaction(null);
                    setDeleteTransactionPassword('');
                    setDeleteTransactionError('');
                  }}
                  sx={{ fontSize: '1.4rem' }}
                  disabled={isDeletingTransaction}
                >
                  ביטול
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={async () => {
                    if (!deleteTransactionPassword) {
                      setDeleteTransactionError('יש להזין סיסמה');
                      return;
                    }

                    try {
                      setIsDeletingTransaction(true);
                      setDeleteTransactionError('');

                      await API.delete('/supplier/transaction/delete/', {
                        data: {
                          transaction_id: deletingTransaction.TrID,
                          password: deleteTransactionPassword,
                        }
                      });

                      // Refresh data
                      fetchTransactions(transactionPaginationModel.page, transactionPaginationModel.pageSize);
                      fetchPurchases(purchasePaginationModel.page, purchasePaginationModel.pageSize);
                      refreshSupplierDetails();

                      // Close dialog
                      setDeleteTransactionDialogOpen(false);
                      setDeletingTransaction(null);
                      setDeleteTransactionPassword('');

                    } catch (error) {
                      console.error('Error deleting transaction:', error);
                      if (error.response?.status === 401) {
                        setDeleteTransactionError('סיסמה שגויה');
                      } else if (error.response?.data?.blocking_items) {
                        const items = error.response.data.blocking_items;
                        setDeleteTransactionError(
                          `לא ניתן למחוק - המלאי הנוכחי לא מספיק: ${items.map(i => i.product_name).join(', ')}`
                        );
                      } else {
                        setDeleteTransactionError(error.response?.data?.error || 'שגיאה במחיקת התנועה');
                      }
                    } finally {
                      setIsDeletingTransaction(false);
                    }
                  }}
                  sx={{ fontSize: '1.4rem' }}
                  disabled={isDeletingTransaction || !deleteTransactionPassword}
                >
                  {isDeletingTransaction ? <CircularProgress size={24} /> : 'מחק תנועה'}
                </Button>
              </DialogActions>
            </MobileDialog>
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default SupplierDetails;